import { ArrowRight, Play } from "lucide-react";
import { NavLink } from "react-router-dom";

import { Button } from "@/components/ui/button";

const heroPillars = [
  {
    title: "Structure",
    description:
      "Modules inherit a shared rule fabric that keeps every action deterministic.",
  },
  {
    title: "Governance",
    description:
      "Policy-aware orchestration ensures human and machine decisions stay aligned.",
  },
  {
    title: "Proof",
    description:
      "Every outcome is captured as verifiable evidence, ready for audit and review.",
  },
  {
    title: "Trust",
    description:
      "Compliance, ethics, and safety are embedded from concept through deployment.",
  },
];

export const HeroSection = () => {
  return (
    <section id="hero" className="relative overflow-hidden pb-28 pt-20">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[540px] w-[540px] -translate-x-1/2 rounded-full bg-stronom-design/25 blur-[160px]" />
      <div className="pointer-events-none absolute -bottom-48 right-[-10%] h-[520px] w-[520px] rounded-full bg-stronom-gov/20 blur-[180px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-16 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="space-y-10">
            <div className="inline-flex items-center gap-3 rounded-full border border-stronom-design/40 bg-stronom-design/10 px-6 py-2 text-xs font-semibold uppercase tracking-[0.4em] text-stronom-design/90 shadow-[0_0_35px_hsl(var(--color-design)_/_0.35)]">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-stronom-design/30">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2Ffa2f15fda5e44932811d4e18209a60ad%2F834c3563665f4e088186da53ecf7a57f?format=webp&width=1200"
                  alt="STRONOM"
                  className="h-8 w-8 brightness-200"
                />
              </span>
              Structured Intelligence Suite
            </div>

            <div className="space-y-6">
              <p className="text-sm uppercase tracking-[0.6em] text-stronom-agents/80">
                STRONOM
              </p>
              <h1 className="font-display text-4xl font-semibold leading-[1.08] text-foreground md:text-6xl">
                The framework for structured intelligence.
              </h1>
              <p className="max-w-xl text-lg text-muted-foreground md:text-xl">
                STRONOM governs how intelligent systems behave, proving every
                output against the rules that created it. Build predictable,
                compliant, and verifiable outcomes across your entire
                organization.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                className="shadow-[0_18px_60px_hsl(var(--color-design)_/_0.35)]"
              >
                <a href="#suite" className="inline-flex items-center gap-2">
                  Explore the Suite
                  <ArrowRight className="size-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-border/60 bg-transparent text-muted-foreground hover:border-stronom-design/60 hover:text-foreground"
              >
                <NavLink
                  to="/design"
                  className="inline-flex items-center gap-2"
                >
                  View Active Module
                  <Play className="size-4" />
                </NavLink>
              </Button>
              <p className="text-sm text-muted-foreground">
                Currently live: STRONOM Design Footwear Studio
              </p>
            </div>

            <div className="grid gap-4 rounded-3xl border border-border/60 bg-card/80 p-6 backdrop-blur md:grid-cols-2">
              {heroPillars.map((item) => (
                <div key={item.title} className="space-y-2">
                  <p className="text-sm font-semibold uppercase tracking-[0.3em] text-stronom-design/80">
                    {item.title}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden w-full justify-center lg:flex">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-72 w-72 rounded-[2.5rem] border border-stronom-design/20 bg-stronom-design/10 blur-3xl" />
            </div>
            <div className="relative flex h-[420px] w-[420px] items-center justify-center">
              <div className="absolute h-full w-full rounded-[2.75rem] border border-white/5 bg-card/70 shadow-[0_40px_160px_rgba(7,12,25,0.6)] backdrop-blur">
                <div className="absolute inset-[18%] rounded-[2rem] border border-stronom-design/30" />
                <div className="absolute inset-[32%] rounded-[1.5rem] border border-stronom-gov/35" />
                <div className="absolute inset-[46%] rounded-[1rem] border border-stronom-media/45" />
                <div className="absolute inset-[18%] animate-[spin_32s_linear_infinite] rounded-[2rem] border border-stronom-design/20" />
                <div className="absolute inset-[32%] animate-[spin_26s_linear_infinite_reverse] rounded-[1.5rem] border border-stronom-gov/25" />
                <div className="absolute inset-[46%] animate-[spin_20s_linear_infinite] rounded-[1rem] border border-stronom-compliance/25" />
                <div className="absolute inset-[38%] flex items-center justify-center rounded-full bg-stronom-design/15 backdrop-blur">
                  <div className="size-16 rounded-2xl bg-gradient-to-br from-stronom-design/90 via-stronom-gov/90 to-stronom-agents/70 shadow-[0_0_60px_hsl(var(--color-design)_/_0.4)]" />
                </div>
              </div>
              <div className="absolute -bottom-10 left-1/2 w-[360px] -translate-x-1/2 rounded-3xl border border-white/10 bg-black/40 px-8 py-6 text-sm text-muted-foreground backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                  Proof-Based Governance
                </p>
                <p className="mt-2 text-foreground">
                  Every module inherits validation primitives, policy locks, and
                  immutable audit trails.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
